import { canEditLayout } from "../../utils/canEditLayout";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Download, Upload, Loader2, Video } from "lucide-react";
import BlockShell from "./BlockShell";
import { Button } from "../ui/button";
import { useAuth, API } from "../../App";
import { absUrl, shareLink, embedSrcFrom } from "./media";
import ComingSoon from "./ComingSoon";
import { sendVideo, stageText } from "../videoSend/sendVideo";
import { readPrefs } from "../videoSend/prefs";
import { SendOptionsButton } from "../videoSend/SendOptions";

const isVideoFile = (f) => /^video\//.test(f.type) || /\.(mp4|mov|m4v|webm)$/i.test(f.name);

const VideoBlock = ({ block, festival, layoutKey: layoutKeyProp }) => {
  const { user } = useAuth();
  const cfg = block.config || {};
  const layoutKey = layoutKeyProp || (festival?.slug ? `festival:${festival.slug}` : null);
  const canEdit = canEditLayout(user, layoutKey);
  const [videoUrl, setVideoUrl] = useState(cfg.video_url || "");
  const [progress, setProgress] = useState(null);
  const pageTitle = festival?.title || block.title || "Video";
  const anchor = `video-${block.block_id}`;

  const upload = async (file) => {
    if (!file || !layoutKey) return;
    if (!isVideoFile(file)) return toast.error("Pick a video file (MP4, MOV or WebM)");
    setProgress({ phase: "read", pct: 0 });
    try {
      const url = (await sendVideo(file, { type: "block", layoutKey }, readPrefs(), setProgress))?.url || "";
      await axios.patch(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/blocks/${block.block_id}`, { config: { ...cfg, video_url: url } }, { withCredentials: true });
      setVideoUrl(url);
      toast.success("Video is live on the page — hit Share to post it");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Upload failed"); } finally { setProgress(null); }
  };

  const embedSrc = !videoUrl ? embedSrcFrom(cfg.embed_url || cfg.embed_code) : "";
  const isBandcamp = /bandcamp\.com\/EmbeddedPlayer/i.test(embedSrc);
  const comingSoon = !videoUrl && !embedSrc && Boolean(cfg.show_coming_soon);
  if (!videoUrl && !embedSrc && !comingSoon && !canEdit) return null;
  const src = absUrl(videoUrl);

  const Uploader = ({ replace }) => (
    <div className="inline-flex flex-wrap gap-2 justify-center" data-testid="video-uploader">
      <label className={`inline-flex items-center gap-2 cursor-pointer rounded-full font-semibold text-sm px-4 py-2 border transition-colors ${progress != null ? "opacity-60 pointer-events-none" : ""} bg-[#B8860B] text-white border-[#B8860B] hover:bg-[#9a7009]`} data-testid="video-record-label">
        {progress != null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
        {progress != null ? <span data-testid="video-block-stage">{stageText(progress) || "Starting…"}</span> : replace ? "Record a new one" : "Record a video now"}
        <input type="file" accept="video/*" capture="environment" className="hidden" disabled={progress != null} onChange={(e) => upload(e.target.files?.[0])} data-testid="video-record-input" />
      </label>
      <label className={`inline-flex items-center gap-2 cursor-pointer rounded-full font-semibold text-sm px-4 py-2 border transition-colors ${progress != null ? "opacity-60 pointer-events-none" : ""} ${replace ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200" : "bg-[#0A1626] text-[#F2CC8F] border-[#0A1626] hover:bg-[#1B3A5C]"}`} data-testid="video-upload-label">
        <Upload className="h-4 w-4" />
        {replace ? "Replace from library" : "Upload from your library"}
        <input type="file" accept="video/mp4,video/quicktime,video/webm,video/*" className="hidden" disabled={progress != null} onChange={(e) => upload(e.target.files?.[0])} data-testid="video-upload-input" />
      </label>
      <SendOptionsButton />
    </div>
  );

  return (
    <section id={anchor} className="max-w-7xl mx-auto px-6 md:px-12 py-8 scroll-mt-24" data-testid="fest-video">
      <BlockShell block={block}>
        {cfg.intro && <p className="text-sm text-[#5C5C5C] mb-4">{cfg.intro}</p>}
        {videoUrl ? (
          <div className="max-w-3xl">
            <video key={videoUrl} src={src} controls playsInline preload="metadata" poster={cfg.poster ? absUrl(cfg.poster) : undefined} className="w-full max-h-[70vh] bg-black rounded-2xl shadow-xl border-2 border-amber-300/70" data-testid="video-player" />
            {cfg.caption && <p className="text-sm text-[#3a3a3a] mt-3 whitespace-pre-line">{cfg.caption}</p>}
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <Button onClick={() => shareLink({ title: pageTitle, text: cfg.caption || `Watch: ${pageTitle}`, url: `${window.location.origin}${window.location.pathname}#${anchor}` })} className="bg-[#0A1626] hover:bg-[#1B3A5C] text-white rounded-full" data-testid="video-share"><Share2 className="h-4 w-4 mr-2" /> Share this video</Button>
              <a href={src} download target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-full" data-testid="video-download"><Download className="h-4 w-4 mr-2" /> Download to post on Instagram / TikTok</Button></a>
              {canEdit && <Uploader replace />}
            </div>
            {progress != null && <div className="mt-3 h-2 w-full rounded-full bg-stone-200 overflow-hidden"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${progress}%` }} /></div>}
          </div>
        ) : embedSrc ? (
          <div className="max-w-3xl">
            <div className={isBandcamp ? "w-full max-w-[420px] h-[120px] rounded-xl overflow-hidden shadow-lg border-2 border-amber-300/70 bg-black" : "relative w-full aspect-video rounded-2xl overflow-hidden shadow-xl border-2 border-amber-300/70 bg-black"}>
              <iframe src={embedSrc} title={block.title || "Video"} className={isBandcamp ? "w-full h-full border-0" : "absolute inset-0 w-full h-full"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" data-testid="video-embed" />
            </div>
            {cfg.caption && <p className="text-sm text-[#3a3a3a] mt-3 whitespace-pre-line">{cfg.caption}</p>}
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <Button onClick={() => shareLink({ title: pageTitle, text: cfg.caption || `Watch: ${pageTitle}`, url: `${window.location.origin}${window.location.pathname}#${anchor}` })} className="bg-[#0A1626] hover:bg-[#1B3A5C] text-white rounded-full" data-testid="video-share"><Share2 className="h-4 w-4 mr-2" /> Share this video</Button>
              {canEdit && <Uploader replace />}
            </div>
          </div>
        ) : comingSoon ? (
          <ComingSoon cfg={cfg} kind="video">
            {canEdit && <><p className="text-xs text-[#5C5C5C] mb-3">Editors: upload the clip here, or paste a YouTube link / embed code in <strong>Edit → Embed</strong>.</p><Uploader /></>}
          </ComingSoon>
        ) : (
          <div className="max-w-3xl rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-8 text-center" data-testid="video-empty-editor">
            <Video className="h-10 w-10 mx-auto text-amber-700 mb-3" />
            <p className="font-extrabold text-[#1A1A1A]">No video yet</p>
            <p className="text-sm text-[#5C5C5C] mt-1 mb-4">Tap <strong>Record a video now</strong> to open your phone's camera, or pick a clip from your library — MP4 or MOV, up to 200MB. It goes live on this page the moment the upload finishes, with Share and Download buttons.</p>
            <Uploader />
            {progress != null && <div className="mt-4 h-2 w-full rounded-full bg-stone-200 overflow-hidden"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${progress}%` }} /></div>}
          </div>
        )}
      </BlockShell>
    </section>
  );
};

export default VideoBlock;
